import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import Tooltip from '@mui/material/Tooltip';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';

import { Dialog, IconButton2, buttonVariants } from '@clatter/ui';
import { useDeepCompareMemo } from '@clatter/platform';
import { StyledError } from './UI';
import { handleAssignItemsToFolder } from '../../../helpers';
import { Button, CircularLoader } from '../../../shared';
import { useAddSubFolderMutation, useUpdateAssetMutation, useUpdateFolderMutation, useUpdatePresentationMutation } from '../../../services';

import { FolderHierarchyPicker } from './FolderHierarchyPicker';
import { FolderManagerDialogAddForm } from './FolderManagerDialogAddForm';

//region INTERFACES
enum actionTypes {
  SUBMIT = 'submit',
  CANCEL = 'cancel',
}

interface IFolderManagerMoveDialogProps {
  submitLabel?: string;
  targetOnly?: boolean;
  includeUserRoot?: boolean;
  dialogState: IMoveDialogState;
  onDismiss: (data: IMoveDialogOnDismiss | null) => void;
  zIndex?: number;
}

//endregion

//region STYLED
const StyledContainer = styled.div`
  width: 50vw;
  max-width: 900px;
  height: 40vh;
  max-height: 620px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;

  .form-control {
    min-height: 80px;
  }

  .header {
    border-bottom: 1px solid lightgrey;
  }

  .content {
    margin-bottom: auto;
  }

  .footer {
  }

  .buttons {
    width: 100%;
    display: flex;
    justify-content: space-between;
    gap: 15px;

    .right-section,
    .left-section {
      flex: 1;
      display: flex;
      gap: 20px;
    }

    .right-section {
      justify-content: flex-end;
    }
  }
`;

export const FolderManagerMoveDialog = ({
  dialogState,
  submitLabel,
  onDismiss,
  zIndex,
  targetOnly = false,
  includeUserRoot = false,
}: IFolderManagerMoveDialogProps) => {
  const [addSubFolder, { isLoading: isAddSubFolderLoading }] = useAddSubFolderMutation();
  const [updateFolder, { isLoading: isUpdateFolderLoading }] = useUpdateFolderMutation();
  const [updateAsset, { isLoading: isUpdateAssetLoading }] = useUpdateAssetMutation();
  const [updatePresentation, { isLoading: isUpdatePresentationLoading }] = useUpdatePresentationMutation();

  //region STATE
  const [selectedFolder, setSelectedFolder] = useState<FolderManagerItem | null>(null);
  const [currentFolder, setCurrentFolder] = useState<FolderManagerItem | null>(() =>
    includeUserRoot ? null : dialogState.rootFolder || null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showAddFolder, setShowAddFolder] = useState(false);

  const title =
    dialogState?.selectedItems?.length == 1
      ? `Move: "${dialogState.selectedItems[0].name}".`
      : `Move ${dialogState.selectedItems.length} items. `;
  //endregion

  const selectedIds = useDeepCompareMemo(
    () => (dialogState.selectedItems || []).map((item) => item.id),
    [dialogState?.selectedItems],
  );

  const selectedTarget = selectedFolder || currentFolder;

  const isLoading = isUpdateFolderLoading || isUpdateAssetLoading || isUpdatePresentationLoading;

  // disable submit if:
  // - loading
  // - no target selected
  // - trying to move a folder into itself (only check folders, not presentations/assets)
  const isMovingFolderIntoItself = !!selectedTarget && !!dialogState.selectedItems?.some(
    (item) => item.type === 'folder' && item.id === selectedTarget.id
  );

  const isSubmitDisabled = isLoading || !selectedTarget || isMovingFolderIntoItself;

  //region METHODS
  const handleDialogDismiss = useCallback(
    async (actionType: actionTypes) => {
      if (actionType === actionTypes.SUBMIT && selectedTarget?.id) {
        if (targetOnly) {
          onDismiss({
            destinationId: selectedTarget.id,
            status: 'success',
            message: '',
          });

          return;
        }

        const result = await handleAssignItemsToFolder({
          target: { id: selectedTarget.id, name: selectedTarget.name },
          selectedItems: dialogState.selectedItems,
          updateFolderCb: updateFolder,
          updateAssetCb: updateAsset,
          updatePresentationCb: updatePresentation,
        });

        if (result?.errors?.length) {
          setErrorMessage('Error moving items to the selected folder!');
          return;
        }

        onDismiss({
          status: 'success',
          message: `Successfully moved ${dialogState?.selectedItems?.length} to the "${selectedTarget?.name}" folder`,
        });
      }

      onDismiss(null);
    },
    [selectedTarget?.id],
  );

  const handleAddFormAction = useCallback(
    async (actionType: string, data: any) => {
      const createInId = selectedTarget?.id || dialogState.rootFolder?.id;

      if (!createInId) {
        console.error('Error creating new folder');
        return;
      }

      if (actionType === actionTypes.SUBMIT && data && createInId) {
        await addSubFolder({ parentId: createInId, data: data });
      }
      setShowAddFolder(false);
    },
    [selectedTarget?.id, dialogState.rootFolder?.id],
  );
  //endregion

  if (!dialogState?.opened) {
    return null;
  }

  return (
    <>
      <Dialog
        title={title}
        onCloseDialog={() => handleDialogDismiss(actionTypes.CANCEL)}
        open={dialogState?.opened}
        zIndex={zIndex}
        noBorder
        includeFooter
      >
      <StyledContainer>
        <div className="content">
          {isLoading ? (
            <CircularLoader />
          ) : (
            <FolderHierarchyPicker
              disabledIds={selectedIds}
              selectedFolder={currentFolder}
              rootFolder={dialogState?.rootFolder}
              includeUserRoot={includeUserRoot}
              onChange={setCurrentFolder}
              onSelect={setSelectedFolder}
            />
          )}
        </div>

        <div className="footer">
          {errorMessage && <StyledError>{errorMessage}</StyledError>}

          {showAddFolder ? (
            <FolderManagerDialogAddForm
              onAction={handleAddFormAction}
              submitLabel="Add Folder"
              title="Add New Folder"
            />
          ) : (
            <div className="buttons">
              <div className="left-section">
                {!!currentFolder && (
                  <Tooltip title="Add Folder" placement="top">
                    {/*@ts-ignore*/}
                    <IconButton2 onClick={() => setShowAddFolder(true)}>
                      <CreateNewFolderOutlinedIcon />
                    </IconButton2>
                  </Tooltip>
                )}
              </div>

              <div className="right-section">
                <Button
                  testId="folder-manager-move-dialog-cancel-button"
                  onClick={() => handleDialogDismiss(actionTypes.CANCEL)}
                  variant={buttonVariants.clear}
                  type="button"
                >
                  Cancel
                </Button>

                <Button
                  testId="folder-manager-move-dialog-submit-button"
                  onClick={() => handleDialogDismiss(actionTypes.SUBMIT)}
                  variant={buttonVariants.primary}
                  disabled={isSubmitDisabled}
                >
                  {submitLabel || 'Move'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </StyledContainer>
      </Dialog>
    </>
  );
};
